import React, { useEffect, useMemo, useState } from "react";
import { Popconfirm, Row, Col, Tag, Dropdown, Menu } from "antd";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { getApi } from "redux/sagas/getApiDataSaga";
import { SERVER_IP } from "assets/Config";
import { deleteApi } from "redux/sagas/deleteApiSaga";
import { resetApiStatus } from "redux/reducers/globals/globalActions";
import HighlightComponent from "components/HighlightComponent";
import {
	ACTIONS,
	API_STATUS,
	DATE_FORMAT,
	STATUS,
} from "constants/app-constants";
import { downloadFileFromURL, generatePagination } from "helpers";
import {
	CloseOutlined,
	DownloadOutlined,
	EditOutlined,
	MoreOutlined,
	EyeOutlined,
} from "@ant-design/icons";
import { getDateFormat } from "services/Utils";
import PdfViewerModal from "components/pdf-viewer-modal";
import BookingsListPresentational from "./bookings-list-presentational";

const initialPageSize = 20;
const intialPageSizeOptions = [20, 40, 80];

const BookingsListFunctional = React.memo((props) => {
	const loadsRedux = useSelector((state) => state.loadsRedux);
	const users = useSelector((state) => state?.userRedux?.users);
	const globalRedux = useSelector((state) => state.globalRedux);
	const [visible, toggleVisible] = useState(false);
	const [searchKey, setSearchKey] = useState("");
	const [selectedRow, setSelectedRow] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [tableData, setTableData] = useState(loadsRedux?.loads || []);
	const [currentPage, setCurrentPage] = useState(1);
	const [state, setState] = useState({
		selectedRow: null,
		visible: false,
	});
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const dispatch = useDispatch();

	const getLoads = () => {
		dispatch(
			getApi(
				ACTIONS.GET_LOADS,
				`${SERVER_IP}booking?orgId=${globalRedux.selectedOrganization._id}&status=InTransit,Halting`
			)
		);
	};

	useEffect(() => {
		let doIt = false;
		if (globalRedux.apiStatus.DELETE_LOAD === "SUCCESS") {
			dispatch(resetApiStatus("DELETE_LOAD"));
			doIt = true;
		}
		if (doIt) {
			getLoads();
		}
	}, [globalRedux.apiStatus]);

	useEffect(() => {
		getLoads();
		generatePagination(tableData);
	}, []);

	useEffect(() => {
		setTableData(loadsRedux?.loads);
	}, [loadsRedux?.loads]);

	const filteredData = useMemo(() => {
		if (searchKey === "") {
			return tableData;
		}
		const searchLower = searchKey.toLowerCase();
		return tableData.filter((record) => {
			const checkField = (field) =>
				field ? field.toString().toLowerCase().includes(searchLower) : false;
			return (
				checkField(record?.trackingNumber) || // LR#
				checkField(
					moment(record?.bookingDate).format(DATE_FORMAT.DD_MM_YYYY)
				) || // LR Date
				checkField(record?.consignor?.displayName) ||
				checkField(record?.consignee?.displayName) ||
				checkField(record?.from?.cityName) ||
				checkField(record?.to?.cityName) ||
				checkField(record?.vehicleId?.vehicleNumber) ||
				checkField(record?.customerFreight) ||
				checkField(record?.status)
			);
		});
	}, [tableData, searchKey]);

	const handleEdit = (rowData) => {
		setState({
			...state,
			selectedRow: rowData,
			visible: true,
		});
	};

	const handleMenuClick = (e, row) => {
		if (e.key === "edit") {
			handleEdit(row); // Handle Edit action
		} else if (e.key === "view") {
			// handleViewRow(row); // Handle View action
		} else if (e.key === "preview") {
			setSelectedFile(row); // Handle View action
		} else if (e.key === "download") {
			downloadFileFromURL(
				`${SERVER_IP}/invoice/download/${row?._id}?orgId=${globalRedux.selectedOrganization._id}`
			);
		}
		// Add other cases as needed
	};

	const menu = (row) => (
		<Menu
			onClick={(e) => {
				console.log("🚀 ~ BookingsListFunctional ~ e:", e);
				// Prevent the row click from being triggered
				e.domEvent.stopPropagation(); // Stop the event propagation in the DOM
				handleMenuClick(e, row);
			}}
		>
			<Menu.Item key="edit">
				<EditOutlined /> Edit Invoice
			</Menu.Item>
			<Menu.Item key="preview">
				<EyeOutlined /> Preview Invoice
			</Menu.Item>
			{/* <Menu.Item key="download">
				<DownloadOutlined /> Download Invoice
			</Menu.Item> */}
		</Menu>
	);

	const column = [
		{
			title: "LR#",
			dataIndex: "trackingNumber", // assuming LR# = trackingNumber
			key: "trackingNumber",
			fixed: "left",
			width: 80,
			sorter: (a, b) => a?.trackingNumber?.localeCompare(b?.trackingNumber),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value || "-"}
				/>
			),
		},
		{
			title: "LR Date",
			dataIndex: "bookingDate",
			key: "bookingDate",
			width: 100,
			sorter: (a, b) => new Date(a.bookingDate) - new Date(b.bookingDate),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={getDateFormat(value, DATE_FORMAT.DD_MM_YYYY) || "-"}
				/>
			),
		},
		{
			title: "Consignor",
			dataIndex: "consignor",
			key: "consignor",
			width: 120,
			sorter: (a, b) =>
				a?.consignor?.displayName?.localeCompare(b?.consignor?.displayName),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value?.displayName || "-"}
				/>
			),
		},
		{
			title: "Consignee",
			dataIndex: "consignee",
			key: "consignee",
			width: 120,
			sorter: (a, b) =>
				a?.consignee?.displayName?.localeCompare(b?.consignee?.displayName),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value?.displayName || "-"}
				/>
			),
		},
		{
			title: "From",
			dataIndex: "from",
			key: "from",
			width: 150,
			sorter: (a, b) => a?.from?.cityName?.localeCompare(b?.from?.cityName),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value?.cityName}
				/>
			),
		},
		{
			title: "To",
			dataIndex: "to",
			key: "to",
			width: 150,
			sorter: (a, b) => a?.to?.cityName?.localeCompare(b?.to?.cityName),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value?.cityName}
				/>
			),
		},
		{
			title: "Vehicle",
			dataIndex: "vehicleId",
			key: "vehicleId",
			width: 100,
			sorter: (a, b) =>
				a?.vehicleId?.vehicleNumber?.localeCompare(b?.vehicleId?.vehicleNumber),
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value?.vehicleNumber?.toString() || "-"}
				/>
			),
		},
		{
			title: "Freight Amount",
			dataIndex: "customerFreight",
			key: "customerFreight",
			align: "right",
			width: 150,
			sorter: (a, b) => a.customerFreight - b.customerFreight,
			render: (value) => (
				<HighlightComponent
					highlightClassName="highlightClass"
					searchWords={[searchKey]}
					autoEscape={true}
					textToHighlight={value?.toString() || "-"}
				/>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 100,
			render: (value) =>
				value ? <Tag color={STATUS[value]}>{value}</Tag> : null,
		},
		{
			title: "Action",
			align: "center",
			key: "action",
			width: 100,
			fixed: "right",
			render: (value, row) => (
				<Row gutter={10} justify="center">
					<Col>
						<Dropdown overlay={menu(row)} trigger={["click"]}>
							<a
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
								}}
							>
								<MoreOutlined />
							</a>
						</Dropdown>
					</Col>
				</Row>
			),
		},
	];

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1)
			return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	const tableLoading = useMemo(
		() => globalRedux.apiStatus.GET_TICKETS === API_STATUS.PENDING,
		[globalRedux.apiStatus]
	);

	const onSelectChange = (selectedRowKeys) => {
		setSelectedRowKeys(selectedRowKeys);
	};

	const rowSelection = {
		selectedRowKeys,
		onChange: onSelectChange,
	};

	return (
		<>
			<BookingsListPresentational
				{...{
					column,
					filteredData,
					visible,
					toggleVisible,
					handleTableChange,
					getStartingValue,
					getEndingValue,
					pageSize,
					intialPageSizeOptions,
					initialPageSize,
					currentPage,
					setSearchKey,
					refreshList: getLoads,
					tableLoading,
					rowSelection,
					users,
					state,
					setState,
					selectedRowKeys,
					selectedRow,
					setSelectedRow,
				}}
			/>
			<PdfViewerModal
				selectedFile={selectedFile}
				visible={!!selectedFile}
				onClose={() => setSelectedFile(null)}
				url={selectedFile}
			/>
		</>
	);
});

export default BookingsListFunctional;
